import { FC, useMemo, useState } from 'react';

import { SxProps, TableCell, TableRow, Theme } from '@mui/material';
import { useFilterStore, useSettingsStore } from 'app/store';

import { useUrlParams } from 'shared/hooks/useUrlParams';
import { TFiltersMethod } from 'shared/services/types/common.types';
import { TCompare } from 'shared/services/types/compare.types';
import { MethodTableLayout } from 'shared/ui/method-table-layout';
import { TableContentWrapper } from 'shared/ui/table-content-wrapper';
import { TableSettingsPopover } from 'shared/ui/table-settings';

import { APPLICATIONS_COMPARE } from '../default-columns/APPLICATIONS_COMPARE';
import { TRANSACTIONS_COMPARE } from '../default-columns/TRANSACTIONS_COMPARE';

import { useGetCompare } from './api';
import { AppComparePart, TransComparePart } from './slices';

interface IProps {
  matchTransaction: (id: number) => void;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  methodID: string;
  onSubmitFilters: (data: TFiltersMethod) => void;
  openMessagesModal: (message: string, time?: string) => void;
}

const customStyles = {
  tableLayout: 'fixed',
  p: '12px 16px',
  position: 'absolute',
  top: '0',
  left: '0',
  '.MuiTableCell-root:first-child': {
    paddingLeft: '0px !important'
  },
  '.MuiTableRow-head': {
    '.MuiTableCell-head': {
      paddingTop: '4px',
      paddingBottom: '10px'
    }
  }
} as SxProps<Theme>;

export const CompareTables: FC<IProps> = ({
  matchTransaction,
  pageSize,
  setPageSize,
  methodID,
  onSubmitFilters,
  openMessagesModal
}) => {
  const { filter } = useFilterStore();
  const { tableColumns } = useSettingsStore();

  const { paramsObj } = useUrlParams();

  const page = paramsObj.page ? parseInt(paramsObj.page) - 1 : 0;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const filterC = useMemo(() => {
    return {
      size: pageSize,
      page: page,
      method_id: methodID,
      status: filter.status,
      subagent_id: filter.subagent_id,
      account_id: filter.account_id,
      remarks: filter.remarks,
      date_from: filter.date_from,
      date_to: filter.date_to
    } as TCompare.GetCompare;
  }, [onSubmitFilters, page, pageSize]);

  const { isCompareLoading, isCompareFetching, compare } = useGetCompare(filterC);

  return (
    <MethodTableLayout
      pageSize={pageSize}
      setPageSize={setPageSize}
      isDataLoading={isCompareFetching || isCompareLoading}
      total={compare?.total || 0}>
      <TableContentWrapper
        columns={[...APPLICATIONS_COMPARE, ...tableColumns, ...TRANSACTIONS_COMPARE]}
        setAnchorEl={setAnchorEl}
        sx={customStyles}>
        <TableSettingsPopover anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
        {compare?.items.map((row, id) => {
          return (
            <TableRow key={id}>
              <TransComparePart matchTransaction={matchTransaction} row={row} />
              {tableColumns.map((column: any) => {
                if (column.hideable) return null;
                const fieldNameApplication =
                  (row.application_status === null ? 'transaction_' : 'application_') +
                  column.field;

                const fieldNameTransaction =
                  (row.transaction_status === null ? 'application_' : 'transaction_') +
                  column.field;
                return (
                  <TableCell
                    align='left'
                    sx={{
                      wordWrap: 'break-word'
                    }}>
                    {
                      //@ts-ignore
                      row[fieldNameApplication]
                    }
                  </TableCell>
                );
              })}
              <AppComparePart openMessagesModal={openMessagesModal} row={row} />
            </TableRow>
          );
        })}
      </TableContentWrapper>
    </MethodTableLayout>
  );
};
